import { useAtomValue } from 'jotai'
import React from 'react'
import * as R from 'remeda'

import { type DisplayRowId } from '~/apps/ninesegsAnalyzer/utils/standardAnalysis'
import { type Brand } from '~/hooks/useSurveyQuery'
import { type DisplayRowItem, QuestionOp } from '~/utils'

import { hiddenRowIdsAtom } from '../atoms'

import { useFiveSegmentsAnswersArrayData } from './useFiveSegmentsAnswersArrayData'
import { useFiveSegmentsCustomerAttributesArrayData } from './useFiveSegmentsCustomerAttributesArrayData'
import { useNineSegmentsAnswersArrayData } from './useNineSegmentsAnswersArrayData'
import { useNineSegmentsCustomerAttributesArrayData } from './useNineSegmentsCustomerAttributesArrayData'

export const useDisplayRowItem = (
  brand: Brand,
  tableType: '5segs' | '9segs',
): DisplayRowItem<DisplayRowId>[] => {
  const hiddenRowIds = useAtomValue(hiddenRowIdsAtom)

  const fiveAnswersArrayData = useFiveSegmentsAnswersArrayData(brand)
  const nineAnswersArrayData = useNineSegmentsAnswersArrayData(brand)
  const fiveAnswersCustomerAttributesArrayData =
    useFiveSegmentsCustomerAttributesArrayData(brand)
  const nineAnswersCustomerAttributesArrayData =
    useNineSegmentsCustomerAttributesArrayData(brand)

  return React.useMemo(() => {
    const answersArrayData = {
      '5segs': fiveAnswersArrayData,
      '9segs': nineAnswersArrayData,
    }[tableType]
    const customerAttributesArrayData = {
      '5segs': fiveAnswersCustomerAttributesArrayData,
      '9segs': nineAnswersCustomerAttributesArrayData,
    }[tableType]

    return R.concat(
      answersArrayData.map((answersData) => ({
        label: QuestionOp.toText(answersData[0].question),
        subItems: answersData.map((answerData) => {
          const id = `${answerData.question.question_id}-${answerData.answerOption.option}`

          return {
            id,
            isVisible: !hiddenRowIds.includes(id),
            label: answerData.answerOption.text,
          }
        }),
      })),
      customerAttributesArrayData.map((customerAttributesData) => ({
        label: customerAttributesData[0].customerAttributeGroup.name,
        subItems: customerAttributesData.map((customerAttributeData) => {
          const id = customerAttributeData.customerAttribute.id

          return {
            id,
            isVisible: !hiddenRowIds.includes(id),
            label: customerAttributeData.customerAttribute.name,
          }
        }),
      })),
    )
  }, [
    fiveAnswersArrayData,
    fiveAnswersCustomerAttributesArrayData,
    hiddenRowIds,
    nineAnswersArrayData,
    nineAnswersCustomerAttributesArrayData,
    tableType,
  ])
}
